import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import * as Sentry from "@sentry/react";
import "./FloatingSupport.css";

const FloatingSupport = ({ inline = false, auth = false }) => {
	const [supportPosition, setSupportPosition] = useState(null);
	const [actionPositions, setActionPositions] = useState({});
	const [isDraggingSupport, setIsDraggingSupport] = useState(false);
	const [draggingAction, setDraggingAction] = useState(null);
	const [isHelpCenterOpen, setIsHelpCenterOpen] = useState(false);
	const [isOverlayOpen, setIsOverlayOpen] = useState(false);
	const [overlayPosition, setOverlayPosition] = useState(null);
	const [closeButtonPos, setCloseButtonPos] = useState(null);
	const supportActionsRef = useRef(null);
	const supportButtonRefs = useRef({});
	const supportDragRef = useRef(null);
	const suppressSupportClickRef = useRef(false);
	const beaconInitRef = useRef(false);

	const hideBeaconCloseButtons = () => {
		const styleCloseButtonsIn = (root) => {
			if (!root?.querySelectorAll) {
				return;
			}

			const beaconContainer = root.querySelector("#beacon-container");

			if (!beaconContainer) {
				return;
			}

			const candidates = beaconContainer.querySelectorAll(
				'button, [role="button"], a, [aria-label*="close" i], [title*="close" i], [class*="close" i]'
			);

			Array.from(candidates).forEach((element) => {
				const label = `${element.textContent || ""} ${element.getAttribute("aria-label") || ""} ${element.getAttribute("title") || ""} ${element.className || ""}`.toLowerCase();

				if (label.includes("close")) {
					element.style.setProperty("display", "none", "important");
				}
			});
		};

		styleCloseButtonsIn(document);

		document.querySelectorAll("iframe").forEach((iframe) => {
			try {
				styleCloseButtonsIn(iframe.contentDocument);
			} catch (e) {
				// Cross-origin frames cannot be styled from here.
			}
		});
	};

	// Load position from localStorage on mount (only if floating mode)
	useEffect(() => {
		if (!inline) {
			const savedPosition = localStorage.getItem('supportButtonPosition');
			if (savedPosition) {
				try {
					setSupportPosition(JSON.parse(savedPosition));
				} catch (e) {
					console.error('Failed to parse saved position:', e);
				}
			}
		}
	}, [inline]);

	// Save position to localStorage whenever it changes (only if floating mode)
	useEffect(() => {
		if (supportPosition && !inline) {
			localStorage.setItem('supportButtonPosition', JSON.stringify(supportPosition));
		}
	}, [supportPosition, inline]);

	useEffect(() => {
		if (!inline || auth) {
			return undefined;
		}

		const updateOverlayState = () => {
			const overlayIsOpen = Boolean(
				document.body.classList.contains("modal-open")
				|| document.querySelector(".offcanvas.show, .modal.show, .offcanvas-backdrop.show, .modal-backdrop.show")
			);

			if (overlayIsOpen) {
				const rect = supportActionsRef.current?.getBoundingClientRect();

				if (rect) {
					setOverlayPosition({ left: rect.left, top: rect.top });
				}
			} else {
				setOverlayPosition(null);
			}

			setIsOverlayOpen(overlayIsOpen);
		};

		updateOverlayState();

		const observer = new MutationObserver(updateOverlayState);
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ["class"],
			childList: true,
			subtree: true,
		});

		window.addEventListener("resize", updateOverlayState);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", updateOverlayState);
		};
	}, [auth, inline]);

	// Initialize Beacon
	useEffect(() => {
		if (window.Beacon && !beaconInitRef.current) {
			beaconInitRef.current = true;
			
			window.Beacon('config', {
				display: {
					position: 'bottom-right',
					zIndex: 9999,
				},
			});

			// Listen for Beacon events
			window.Beacon('on', 'open', () => {
				setIsHelpCenterOpen(true);
				window.setTimeout(hideBeaconCloseButtons, 50);
				window.setTimeout(hideBeaconCloseButtons, 300);
			});

			window.Beacon('on', 'close', () => {
				setIsHelpCenterOpen(false);
			});
		}
	}, []);

	// Dynamic positioning for custom close button
	useEffect(() => {
		if (!isHelpCenterOpen) {
			setCloseButtonPos(null);
			return;
		}

		let rafId;
		const updatePosition = () => {
			const beaconIframe = document.querySelector("#beacon-container iframe");
			if (beaconIframe) {
				const rect = beaconIframe.getBoundingClientRect();
				if (rect.width > 0 && rect.height > 0) {
					// Place close button at the top-right corner of the beacon
					const top = Math.max(10, rect.top - 15);
					const left = Math.min(window.innerWidth - 34 - 10, rect.right - 15); // 34px is button width, 10px is edge padding
					
					setCloseButtonPos((prev) => {
						// Only update state if position changed significantly to avoid jitter
						if (!prev || Math.abs(prev.top - top) > 1 || Math.abs(prev.left - left) > 1) {
							return { top, left };
						}
						return prev;
					});
				}
			}
			rafId = requestAnimationFrame(updatePosition);
		};
		updatePosition();

		return () => cancelAnimationFrame(rafId);
	}, [isHelpCenterOpen]);

	const handleHelpCenter = () => {
		if (suppressSupportClickRef.current) {
			return;
		}

		if (window.Beacon) {
			const actionRect = supportActionsRef.current?.getBoundingClientRect();
			const position = actionRect && actionRect.left < window.innerWidth / 2 ? 'left' : 'right';

			window.Beacon('config', { display: { position } });

			// Toggle Beacon - if open, close it; if closed, open it
			if (isHelpCenterOpen) {
				window.Beacon('close');
			} else {
				window.Beacon('open');
			}
		}
	};

	const handleCloseHelpCenter = () => {
		if (window.Beacon) {
			window.Beacon('close');
		}

		setIsHelpCenterOpen(false);
	};

	const handleReportBug = async () => {
		if (suppressSupportClickRef.current) {
			return;
		}

		const feedback = Sentry.getFeedback?.();

		if (feedback) {
			const form = await feedback.createForm({
				formTitle: "Report a Bug",
				messageLabel: "Bug details",
				messagePlaceholder: "Tell us what happened and what you expected.",
				submitButtonLabel: "Send report",
			});

			form.appendToDom();
			form.open();
		}
	};

	const handleSupportPointerDown = (e, action) => {
		if (e.button !== undefined && e.button !== 0) {
			return;
		}

		const supportAction = supportButtonRefs.current[action];

		if (!supportAction) {
			return;
		}

		const rect = supportAction.getBoundingClientRect();

		supportDragRef.current = {
			action,
			pointerId: e.pointerId,
			startX: e.clientX,
			startY: e.clientY,
			offsetX: e.clientX - rect.left,
			offsetY: e.clientY - rect.top,
			moved: false,
		};

		// Attach window-level pointer move and up handlers
		window.addEventListener('pointermove', handleSupportPointerMove);
		window.addEventListener('pointerup', handleSupportPointerUp);
		window.addEventListener('pointercancel', handleSupportPointerUp);
	};

	const handleSupportPointerMove = (e) => {
		const dragState = supportDragRef.current;
		const supportAction = dragState ? supportButtonRefs.current[dragState.action] : null;

		if (!dragState || !supportAction || dragState.pointerId !== e.pointerId) {
			return;
		}

		if (!dragState.moved && (Math.abs(e.clientX - dragState.startX) > 3 || Math.abs(e.clientY - dragState.startY) > 3)) {
			dragState.moved = true;
			setIsDraggingSupport(true);
			setDraggingAction(dragState.action);
		}

		if (!dragState.moved) {
			return;
		}

		e.preventDefault();

		const maxLeft = window.innerWidth - supportAction.offsetWidth;
		const maxTop = window.innerHeight - supportAction.offsetHeight;
		const nextLeft = Math.max(0, Math.min(e.clientX - dragState.offsetX, maxLeft));
		const nextTop = Math.max(0, Math.min(e.clientY - dragState.offsetY, maxTop));

		setActionPositions((positions) => ({
			...positions,
			[dragState.action]: { left: nextLeft, top: nextTop },
		}));
	};

	const handleSupportPointerUp = (e) => {
		const dragState = supportDragRef.current;

		if (!dragState || dragState.pointerId !== e.pointerId) {
			return;
		}

		if (dragState.moved) {
			suppressSupportClickRef.current = true;
			window.setTimeout(() => {
				suppressSupportClickRef.current = false;
			}, 150);
		}

		supportDragRef.current = null;
		setIsDraggingSupport(false);
		setDraggingAction(null);

		// Remove window-level event listeners
		window.removeEventListener('pointermove', handleSupportPointerMove);
		window.removeEventListener('pointerup', handleSupportPointerUp);
		window.removeEventListener('pointercancel', handleSupportPointerUp);
	};

	const containerClass = inline
		? `header-support-actions${auth ? " auth-support-actions" : ""}${isOverlayOpen && overlayPosition ? " is-overlay-clear" : ""}${supportPosition ? " is-floating" : ""}${isDraggingSupport ? " is-dragging" : ""}`
		: `floating-support-actions${supportPosition ? " is-floating" : ""}${isDraggingSupport ? " is-dragging" : ""}`;

	const getActionStyle = (action) => {
		const position = actionPositions[action];

		return position ? { left: `${position.left}px`, top: `${position.top}px` } : undefined;
	};

	const getActionClass = (action) => {
		return `header-support-link header-support-link-${action}${actionPositions[action] ? " is-floating" : ""}${draggingAction === action ? " is-dragging" : ""}`;
	};

	const containerStyle = isOverlayOpen && overlayPosition
		? { left: `${overlayPosition.left}px`, top: `${overlayPosition.top}px` }
		: supportPosition ? { left: `${supportPosition.left}px`, top: `${supportPosition.top}px` } : undefined;

	const supportActions = (
		<div
			ref={supportActionsRef}
			className={containerClass}
			style={containerStyle}
		>
			<button 
				ref={(element) => {
					supportButtonRefs.current.help = element;
				}}
				type="button" 
				className={getActionClass("help")}
				style={getActionStyle("help")}
				onPointerDown={(e) => handleSupportPointerDown(e, "help")}
				onClick={handleHelpCenter}
				title={isHelpCenterOpen ? "Close Help Center" : "Help Center"}
			>
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
					<circle cx="12" cy="12" r="10"></circle>
					<path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
					<line x1="12" y1="17" x2="12.01" y2="17"></line>
				</svg>
				<span>Help Center</span>
			</button>
			{false && isHelpCenterOpen && (
				<button
					type="button"
					className="hbr-help-center-close"
					onClick={handleCloseHelpCenter}
					title="Close Help Center"
					aria-label="Close Help Center"
				>
					×
				</button>
			)}
			<button 
				ref={(element) => {
					supportButtonRefs.current.bug = element;
				}}
				type="button" 
				className={getActionClass("bug")}
				style={getActionStyle("bug")}
				onPointerDown={(e) => handleSupportPointerDown(e, "bug")}
				onClick={handleReportBug}
				title="Report a Bug"
			>
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
					<path d="m8 2 1.88 1.88"></path>
					<path d="M14.12 3.88 16 2"></path>
					<path d="M9 7.13v-1a3 3 0 0 1 6 0v1"></path>
					<path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"></path>
					<path d="M12 20v-9"></path>
					<path d="M6 13H2"></path>
					<path d="M22 13h-4"></path>
					<path d="m4.5 17 2.25-2"></path>
					<path d="m19.5 17-2.25-2"></path>
				</svg>
				<span>Report a Bug</span>
			</button>
		</div>
	);

	const closeButtonStyle = closeButtonPos
		? { left: `${closeButtonPos.left}px`, top: `${closeButtonPos.top}px`, display: 'flex' }
		: { display: 'none' };

	const closeButton = isHelpCenterOpen
		? createPortal(
			<button
				type="button"
				className="hbr-help-center-close"
				style={closeButtonStyle}
				onClick={handleCloseHelpCenter}
				title="Close Help Center"
				aria-label="Close Help Center"
			>
				&times;
			</button>,
			document.body
		)
		: null;

	if (inline && !auth && isOverlayOpen && overlayPosition) {
		return (
			<>
				{createPortal(supportActions, document.body)}
				{closeButton}
			</>
		);
	}

	return (
		<>
			{supportActions}
			{closeButton}
		</>
	);
};

export default FloatingSupport;
